import { memo } from "react";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import { USAGE_TYPE_TO_UI_LABEL } from "../utils/questionnaireManagementConstants";
import type {
  QuestionnaireCategoryFilter,
  QuestionnaireStatusFilter,
} from "../utils/questionnaireManagementFilterUtils";

export interface QuestionnaireManagementTabsHeaderProps {
  searchQuery: string;
  statusFilter: QuestionnaireStatusFilter;
  categoryFilter: QuestionnaireCategoryFilter;
  onChangeSearchQuery: (value: string) => void;
  onChangeStatusFilter: (value: QuestionnaireStatusFilter) => void;
  onChangeCategoryFilter: (value: QuestionnaireCategoryFilter) => void;
  onCreateQuestionnaire: () => void;
}

const QuestionnaireManagementTabsHeader = memo(
  ({
    searchQuery,
    statusFilter,
    categoryFilter,
    onChangeSearchQuery,
    onChangeStatusFilter,
    onChangeCategoryFilter,
    onCreateQuestionnaire,
  }: QuestionnaireManagementTabsHeaderProps) => {
    return (
      <Stack spacing={1.25}>
        <Tabs
          value={categoryFilter}
          onChange={(_event, nextCategory: QuestionnaireCategoryFilter) =>
            onChangeCategoryFilter(nextCategory)
          }
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Todos" value="all" />
          <Tab label={USAGE_TYPE_TO_UI_LABEL.triage} value="triage" />
          <Tab
            label={USAGE_TYPE_TO_UI_LABEL.preConsultation}
            value="preConsultation"
          />
          <Tab
            label={USAGE_TYPE_TO_UI_LABEL.postConsultation}
            value="postConsultation"
          />
          <Tab label={USAGE_TYPE_TO_UI_LABEL.attendance} value="attendance" />
        </Tabs>

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <TextField
            fullWidth
            size="small"
            variant="filled"
            label="Buscar questionário"
            placeholder="Filtre por nome do questionário"
            value={searchQuery}
            onChange={(event) => onChangeSearchQuery(event.target.value)}
            sx={{ maxWidth: { md: 520 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="flex-end"
            sx={{ flexShrink: 0 }}
          >
            <Box
              sx={{
                display: "inline-flex",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: 0.25,
              }}
            >
              <ToggleButtonGroup
                exclusive
                value={statusFilter}
                onChange={(
                  _event,
                  nextStatus: QuestionnaireStatusFilter | null,
                ) => {
                  if (!nextStatus) return;
                  onChangeStatusFilter(nextStatus);
                }}
                sx={{
                  height: 32,
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    px: 1.5,
                    border: "none",
                    borderRadius: 2,
                    color: "text.secondary",
                    backgroundColor: "transparent",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    color: "primary.main",
                    backgroundColor: "action.hover",
                    fontWeight: 600,
                  },
                }}
              >
                <ToggleButton value="all">Todos</ToggleButton>
                <ToggleButton value="active">Ativos</ToggleButton>
                <ToggleButton value="draft">Rascunhos</ToggleButton>
                <ToggleButton value="unknown">Em edição</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Button
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              sx={{
                height: 36,
                px: 2,
                borderRadius: 2,
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
              onClick={onCreateQuestionnaire}
            >
              Novo questionário
            </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  },
);

QuestionnaireManagementTabsHeader.displayName =
  "QuestionnaireManagementTabsHeader";

export default QuestionnaireManagementTabsHeader;
